<template>
  <BoxDetailsDrawerSectionCard
    :loading="commentsLoading"
    :title="t('box_details.details_drawer.comments')"
  >
    <template #title-append>
      <template v-if="commentsSize > 0">
        <div class="d-flex align-center ga-2">
          <VBadge
            color="primary-lighten-5"
            :content="commentsSize"
            inline
            text-color="primary"
          />
          <VIconBtn
            icon="far fa-rotate"
            :loading="refreshing"
            name="btn-box-details-drawer-comment-reload"
            size="small"
            variant="text"
            @click="onClickRefresh"
          />
        </div>
      </template>
    </template>
    <template #append>
      <VBtnPrimary
        v-if="!commentsLoading && commentsSize === 0 && !forceShowComments"
        data-test-id="file-details-comment-add"
        name="btn-box-details-drawer-add-comments"
        variant="text"
        @click="addFirstComment"
      >
        {{ t('box_details.details_drawer.sections.comments.text_add') }}
      </VBtnPrimary>
    </template>
    <template #content>
      <DKitDeepBoxComments
        v-show="commentsSize > 0 || forceShowComments || refreshing"
        ref="commentsRef"
        :api-token="bearer!"
        :can-comment
        :env
        :node-id="nodeId"
        :scroll-el
        @comment:cancel="forceShowComments = false"
        @comments:fetched="commentsFetched = true"
        @comments:rendered="emit('ready')"
        @comments:size="onUpdateSize"
        @loading="commentsLoading = $event"
      >
        <template #loading><div></div></template>
        <template v-if="genericFolder" #generic-user-title>
          {{ genericFolder.displayName }}
        </template>
        <template v-if="genericFolder" #generic-avatar>
          <BoxGenericsAvatar :generic-folder="genericFolder" />
        </template>
      </DKitDeepBoxComments>
    </template>
  </BoxDetailsDrawerSectionCard>
</template>

<script setup lang="ts">
import { nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGoTo } from 'vuetify'
import { DKitDeepBoxComments } from '@deepcloud/deep-ui-kit'
import { useKeycloak } from '@/composables/auth/keycloak.ts'
import BoxDetailsDrawerSectionCard from '@/components/box/details-drawer/BoxDetailsDrawerSectionCard.vue'
import BoxGenericsAvatar from '@/components/box/generics/BoxGenericsAvatar.vue'
import type { GenericFolder } from '@deepcloud/api-deep-box'
import type { PropType } from 'vue'

const props = defineProps({
  nodeId: {
    type: String,
    default: '',
  },
  scrollEl: {
    type: Object as PropType<HTMLElement>,
    default: () => ({}),
  },
  canComment: {
    type: Boolean,
    default: false,
  },
  genericFolder: {
    type: Object as PropType<GenericFolder>,
    default: undefined,
  },
})

const emit = defineEmits<{
  'update:size': [number]
  'ready': []
}>()

const { bearer } = useKeycloak()
const env = import.meta.env.VITE_ENVIRONMENT

const { t } = useI18n()
const goTo = useGoTo()

const commentsRef = useTemplateRef('commentsRef')
const commentsSize = ref(0)
const commentsLoading = ref(false)
const commentsFetched = ref(false)
const forceShowComments = ref(false)
const refreshing = ref(false)

async function scrollTo(targetEl: HTMLElement | undefined) {
  await nextTick()
  void goTo(targetEl ?? 0, {
    container: props.scrollEl,
  })
}

async function addFirstComment() {
  forceShowComments.value = true
  await nextTick()
  commentsRef.value?.focusAddComment()

  void scrollTo(commentsRef.value?.$el)
}

async function onClickRefresh() {
  refreshing.value = true
  await commentsRef.value?.refresh()
  refreshing.value = false
}

function onUpdateSize(size: number) {
  commentsSize.value = size

  if (size === 0) {
    // reset so that if no comments are available it hides the comments component
    forceShowComments.value = false
  }

  if (!refreshing.value && commentsFetched.value) {
    emit('update:size', size)
  }
}

watch(
  () => props.nodeId,
  () => {
    commentsFetched.value = false
    forceShowComments.value = false
  },
)

defineExpose({
  addFirstComment,
})
</script>
